// #642 — the Order of Go model.
//
// Pure functions: take the raw entities the app has already prefetched
// (rides, breaks, classes, people, horses, tests) plus the live status map from
// the batch-status poll, and produce the ONE flat, time-ordered list the
// announcer reads top-to-bottom.
//
// Why the merge happens here and not on the server (docs/score/announcer.md,
// "Breaks"): reconcile deliberately never writes a break as a ride — a break has
// no rider, no horse and must never be scored, placed or emailed. So `GET /ride`
// and `GET /break` are two separate lists and the interleave is the client's job.
//
// Ride rows also carry no denormalized names — reconcile stores riderId /
// horseId / classId / testId only — so every display string is joined here from
// the prefetched entity caches.

import { RIDE_STATUSES, isQualifying } from "score-common";

import { foxboxTimeToUtc } from "./foxboxTime";

export const ROW_KIND = {
  CLASS_HEADER: "CLASS_HEADER",
  RIDE: "RIDE",
  BREAK: "BREAK",
};

// Where a ride sits relative to "now" in the running order. Only ever assigned
// to at most one row each.
export const RIDE_ROLE = {
  CURRENT: "CURRENT",
  ON_DECK: "ON_DECK",
  NEXT: "NEXT",
};

// Reconcile only STAMPS a status for scratches; scoring stamps the rest. A ride
// that hasn't been touched has no `status` field at all, so treat missing as
// PENDING rather than rendering an empty badge.
export function normalizeStatus(status) {
  return status || RIDE_STATUSES.PENDING;
}

// Sort missing times LAST rather than first (an unscheduled ride shouldn't jump
// to the top of the announcer's list). Matches GET /break's server-side rule.
const timeKey = (t) => (t ? String(t) : "￿");

const byId = (rows = []) => new Map((rows || []).filter(Boolean).map((r) => [r.id, r]));

function personName(person) {
  if (!person) return "";
  const full = [person.firstName, person.lastName].filter(Boolean).join(" ").trim();
  return full || person.name || "";
}

function joinParts(parts, sep) {
  return parts.filter((p) => p != null && p !== "").join(sep);
}

// #525 registry fields (breed/height/color/.../foalYear) are ALL optional — FVD
// only carries them when HR.hdf has them. "8yo" only for a sane foalYear;
// otherwise "" so the caller can drop the whole line rather than show "NaNyo".
function ageFrom(foalYear, referenceYear) {
  const fy = Number(foalYear);
  if (!Number.isFinite(fy) || fy <= 0) return "";
  const age = referenceYear - fy;
  return age > 0 && age < 60 ? `${age}yo` : "";
}

// Same field fallbacks the printed OOG report uses
// (packages/report-defs/src/score/announcer-order-of-go.js buildRide), so the
// screen and the paper fallback say the same thing about the same ride.
function buildRideRow(ride, { classesById, peopleById, horsesById, testsById, statuses }) {
  const cls = classesById.get(ride.classId);
  const rider = peopleById.get(ride.riderId);
  const horse = horsesById.get(ride.horseId);
  const test = testsById.get(ride.testId);

  // #643 — the ride-detail accordion's script. Composed with the SAME field
  // fallbacks and join order as the printed OOG report
  // (packages/report-defs/src/score/announcer-order-of-go.js buildRide), per
  // docs/score/announcer.md: "render the same fields in the same order ... so
  // the announcer's eye already knows where to look." Owner is a person
  // reference (horse.ownerId), resolved from the SAME prefetched people cache
  // used for the rider — no extra fetch. Each joined string is "" when every
  // part is missing, so RideRow can drop the whole line — never a bare label
  // over an empty value.
  let owner = "";
  if (horse?.ownerId) owner = personName(peopleById.get(horse.ownerId));
  if (!owner) owner = horse?.owner || horse?.ownerName || "";

  const referenceYear = new Date().getFullYear();
  const horseDetail = joinParts(
    [horse?.breed, horse?.height, horse?.color, horse?.sex, ageFrom(horse?.foalYear, referenceYear)],
    " · ",
  );
  const pedigree =
    horse?.sire || horse?.dam
      ? joinParts(
          [
            `${horse?.sire || "?"} × ${horse?.dam || "?"}`,
            horse?.damsSire ? `dam's sire ${horse.damsSire}` : "",
          ],
          " · ",
        )
      : "";
  const ownerLine = joinParts([owner, horse?.breeder ? `breeder ${horse.breeder}` : ""], " · ");
  // #726 — drop ZIP from the "Rider From" line; city/state only.
  const address = joinParts([rider?.city, rider?.state], ", ");

  return {
    kind: ROW_KIND.RIDE,
    id: ride.id,
    // #802 — correct foxbox's wall+8 mislabeled-Z stamp to genuine UTC so
    // display AND findNextEvent's epoch math are right year-round.
    scheduledTime: foxboxTimeToUtc(ride.scheduledTime),
    sortKey: timeKey(foxboxTimeToUtc(ride.scheduledTime)),
    classId: ride.classId,
    className: cls?.className || cls?.name || "",
    classNumber: cls?.classNumber || cls?.number || "",
    competitorNumber: ride.competitorNumber || ride.rideNumber || "",
    riderName: personName(rider) || ride.riderName || "",
    horseName: horse?.name || ride.horseName || "",
    testName: test?.testCode || test?.level || test?.name || "",
    // #806 — ride.qualifying is FVD's chrQualifyID text (e.g. "No" | "Open" |
    // "Adult Amateur" | "JR/YR"), not a boolean. Boolean(ride.qualifying)
    // treated the literal string "No" as truthy, so every ride showed the
    // QUALIFYING badge. isQualifying() (score-common) is the shared fix, also
    // used by report-defs's #686 divisionLabel().
    qualifying: isQualifying(ride.qualifying),
    termReason: ride.termReason || "",
    // #643 ride-detail accordion fields — see comment above.
    horseDetail,
    pedigree,
    ownerLine,
    address,
    // The live poll wins over the (possibly stale) prefetched row.
    status: normalizeStatus(statuses?.[ride.id] ?? ride.status),
    role: null,
    ride,
  };
}

function buildBreakRow(brk) {
  return {
    kind: ROW_KIND.BREAK,
    id: brk.id,
    // #802 — same wall+8 correction as ride rows above.
    scheduledTime: foxboxTimeToUtc(brk.scheduledTime),
    sortKey: timeKey(foxboxTimeToUtc(brk.scheduledTime)),
    label: brk.label || "",
  };
}

// CURRENT = the ride actually in the ring. ON_DECK / NEXT = the first two rides
// still to come after it. Scratched and finished rides are skipped when looking
// ahead — the announcer needs the next horse he'll actually see, not the next
// row in the list.
function assignRoles(rows) {
  const rideRows = rows.filter((r) => r.kind === ROW_KIND.RIDE);
  const currentIdx = rideRows.findIndex((r) => r.status === RIDE_STATUSES.IN_PROGRESS);
  if (currentIdx >= 0) rideRows[currentIdx].role = RIDE_ROLE.CURRENT;

  // Before the first ride of the day nothing is IN_PROGRESS, so the first
  // pending ride is still legitimately "on deck".
  const upcoming = rideRows
    .slice(currentIdx + 1)
    .filter((r) => r.status === RIDE_STATUSES.PENDING);

  if (upcoming[0]) upcoming[0].role = RIDE_ROLE.ON_DECK;
  if (upcoming[1]) upcoming[1].role = RIDE_ROLE.NEXT;
  return rows;
}

/**
 * Merge rides + breaks into one time-ordered list and insert class headers.
 *
 * Class headers are *dividers*, not timed rows: a class's position comes from
 * its rides' times, so a header is emitted wherever the class of the next ride
 * changes. A break falling mid-class does NOT re-emit the header.
 *
 * @returns {Array} rows, each { kind, id, ... } — see ROW_KIND.
 */
export function buildOrderOfGoRows({
  rides = [],
  breaks = [],
  classes = [],
  people = [],
  horses = [],
  tests = [],
  statuses = {},
} = {}) {
  const ctx = {
    classesById: byId(classes),
    peopleById: byId(people),
    horsesById: byId(horses),
    testsById: byId(tests),
    statuses,
  };

  const timed = [
    ...(rides || []).filter(Boolean).map((r) => buildRideRow(r, ctx)),
    ...(breaks || []).filter(Boolean).map(buildBreakRow),
  ].sort((a, b) => {
    if (a.sortKey !== b.sortKey) return a.sortKey < b.sortKey ? -1 : 1;
    // Same minute: keep the running order stable and deterministic.
    if (a.kind !== b.kind) return a.kind === ROW_KIND.RIDE ? -1 : 1;
    return String(a.id).localeCompare(String(b.id));
  });

  const rows = [];
  let lastClassId = null;
  for (const row of timed) {
    if (row.kind === ROW_KIND.RIDE && row.classId && row.classId !== lastClassId) {
      rows.push({
        kind: ROW_KIND.CLASS_HEADER,
        id: `class-header-${row.classId}`,
        classId: row.classId,
        className: row.className,
        classNumber: row.classNumber,
      });
      lastClassId = row.classId;
    }
    rows.push(row);
  }

  return assignRoles(rows);
}

export function findRoleRideId(rows, role) {
  return rows.find((r) => r.kind === ROW_KIND.RIDE && r.role === role)?.id || null;
}

// #691 — the sticky header's countdown: the next scheduled ride OR break,
// whichever comes first. `rows` is already time-sorted by
// buildOrderOfGoRows, so this is just the first RIDE/BREAK row whose
// scheduledTime is still ahead of `nowMs` — a real instant comparison
// (Date math is timezone-agnostic; only DISPLAY needs the venue-zone
// formatter). Class headers are dividers, never a candidate event.
export function findNextEvent(rows, nowMs = Date.now()) {
  for (const row of rows) {
    if (row.kind !== ROW_KIND.RIDE && row.kind !== ROW_KIND.BREAK) continue;
    if (!row.scheduledTime) continue;
    const t = new Date(row.scheduledTime).getTime();
    if (Number.isNaN(t) || t <= nowMs) continue;
    return row;
  }
  return null;
}

// #644 — results/placings. GET /class/:classId/results (score-api) returns one
// entry per Ride: { rideId, classId, placing, composite, status, ... }, already
// sorted (placing asc, unplaced last, then composite desc) — NEVER re-sorted
// here. But it carries no rider/horse/CNO — reconcile stores ids only, same as
// every other entity in this app — so join those back from the SAME ride rows
// already built by buildOrderOfGoRows, exactly like #643's ride-detail fields.
export function buildClassResults(results = [], rideRows = []) {
  const rideById = byId(rideRows);
  return (results || []).map((r) => {
    const row = rideById.get(r.rideId);
    return {
      rideId: r.rideId,
      classId: r.classId,
      placing: r.placing ?? null,
      composite: r.composite,
      status: r.status,
      // #699 — carry the per-judge confirmation detail (score-api #697) through
      // so RideRow/ClassResults can render committed indicators + the CONFIRMED
      // reveal. perJudge under PARTIAL exposes NO percentage (anti-leak), by design.
      perJudge: r.perJudge ?? [],
      confirmedCount: r.confirmedCount ?? 0,
      completedCount: r.completedCount ?? 0,
      expectedJudgeCount: r.expectedJudgeCount ?? 0,
      competitorNumber: row?.competitorNumber || "",
      riderName: row?.riderName || "",
      horseName: row?.horseName || "",
    };
  });
}

// score-api's per-ride results status (#697): scores are WITHHELD while PARTIAL
// (composite null, no per-judge percentage) so one judge's mark never renders as
// the final score; CONFIRMED = every expected judge confirmed their marks (score
// revealed, "ready to announce"); FINAL = signed/admin-confirmed (placing shown).
export const RESULT_STATUS = { PARTIAL: "PARTIAL", CONFIRMED: "CONFIRMED", FINAL: "FINAL" };

// A ride's numeric score is exposed once CONFIRMED or FINAL — never PARTIAL.
export function isResultRevealed(status) {
  return status === RESULT_STATUS.CONFIRMED || status === RESULT_STATUS.FINAL;
}

// #699 — a class's scores become readable once EVERY ride is CONFIRMED-or-FINAL
// (all judges confirmed), not only when every ride is signed FINAL. An
// empty/unfetched list is never revealed: that would flash a misleadingly empty
// table before data even loads (docs/score/announcer.md: "never a half-computed
// or misleading placing").
export function isClassResultsRevealed(results) {
  return Boolean(results && results.length > 0 && results.every((r) => isResultRevealed(r.status)));
}

// A class is FINAL once every ride has a signed/admin-confirmed FINAL result
// (score-api's per-ride FINAL, #506 admin force-final included) — the SAME
// "allFinal" convention scoreadmin-fe's ClassPlacings.jsx uses.
export function isClassResultsFinal(results) {
  return Boolean(results && results.length > 0 && results.every((r) => r.status === RESULT_STATUS.FINAL));
}
